import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import {
	ProductAddOnInterface,
	QuotationInterface,
	MotorAddOnCARTPriceInterface
} from "src/app/comparison/interface/motor-quotation.interface";
import { PAYMENT_OPTION_ATOME, PAYMENT_OPTION_FPX, PAYMENT_OPTION_FPX_ENTERPRISE } from "../constants/payment-options";
import { DIGITAL_ROADTAX_HANDLING_FEE } from "../constants/roadtax";

export function getShopbackinstallmentCharges(
	totalCharge: number,
	totalPromo: number,
) {
	let totalBeforeCharges = totalCharge - totalPromo;
	let charges = (1.5 / 100) * totalBeforeCharges;
	return Number(charges.toFixed(2));
}

export function getPaymentValues(
	isIncludeRoadtax: boolean = false,
	isIncludeDigitalRoadtax: boolean = false,
	requestData: any,
	selectedQuotation: QuotationInterface,
	promo: any,
	addOnFormGroup: UntypedFormGroup,
	selectedAddOn: any[],
	eppRate: number,
	paymentChannel: string,
	bnplTotalServiceTaxRate: number,
	bnplIsSubsidized: boolean,
	cartAddOnPriceList: MotorAddOnCARTPriceInterface[],
	numberOfLltpSeats: number,
	isCarPlusChecked: boolean,
	selectedAvailableInsurerCarPlus: any,
	isPsCarePlusChecked: boolean = false,
	selectedPsCarePlus: any = null
) {
	const {
		serviceTaxRate,
		taxRate,
		stampDutyPrice,
		totalBasePremium,
		platformDiscountRate,
		code
	} = selectedQuotation || {};
	const { roadTaxPrice, roadTaxDeliveryPrice } = requestData || {};

	let grossPremium =
		totalBasePremium + getTotalAddOnPrice(selectedAddOn, addOnFormGroup, cartAddOnPriceList) || 0;

	if (isCarPlusChecked)
		grossPremium += Number(selectedAvailableInsurerCarPlus.evaluate);
	
	const taxAmount = Number((grossPremium * taxRate).toFixed(2)) || 0;
	let totalNetPremium =
		Number(grossPremium.toFixed(2)) + Number(taxAmount.toFixed(2)) + stampDutyPrice || 0;
	totalNetPremium = (code != null && (code.toLowerCase() === "takaful_malaysia" || code.toLowerCase() === "allianz")) ? customSTMBRound(totalNetPremium) : totalNetPremium;
	const platformDiscountRateAmount =
		Number((platformDiscountRate * grossPremium).toFixed(2)) || 0;
	let roadtaxAmount = null;

	// Digital road tax has its own handling fee; otherwise add delivery price
	if (isIncludeRoadtax && !isIncludeDigitalRoadtax)
		roadtaxAmount = roadTaxPrice + roadTaxDeliveryPrice;
	else if (isIncludeRoadtax && isIncludeDigitalRoadtax)
		roadtaxAmount = roadTaxPrice + DIGITAL_ROADTAX_HANDLING_FEE;

	// Care+ is priced flat and is not part of the SST base, so it's added here
	// alongside the non-SST add-ons rather than into grossPremium.
	const psCarePlusFee =
		isPsCarePlusChecked && selectedPsCarePlus
			? getAddOnPrice(selectedPsCarePlus)
			: 0;

	const totalCharge =
		Number(
			(totalNetPremium - platformDiscountRateAmount + roadtaxAmount + getTotalNonSSTAddOnPrice(selectedAddOn, numberOfLltpSeats) + psCarePlusFee).toFixed(2),
		) || 0;

	const totalPromo = Number(getTotalPromo(promo, grossPremium).toFixed(2)) || 0;

	let actualServiceTaxRate = serviceTaxRate;
	let bnplServiceTaxRate = 0;
	let bnplServiceFee = 0;
	let serviceFee = 0;
	
	if(requestData.isOnlinePayment) {
		// BNPL Service Charges Calculation
		if (paymentChannel == PAYMENT_OPTION_ATOME){
			if (!bnplIsSubsidized){
				bnplServiceTaxRate = bnplTotalServiceTaxRate - actualServiceTaxRate;
				bnplServiceFee = Number((bnplServiceTaxRate * (totalCharge - totalPromo)).toFixed(2)) || 0;
			}
		}

		serviceFee = Number((actualServiceTaxRate * (totalCharge - totalPromo)).toFixed(2)) || 0;

		//[A2C-498]
		//	- applies to all DR types
		//  - when FPX is selected, service fee is RM 1 (from older Jira A2C-353)
		//  - when FPX Enterprise is selected, service fee is RM 1.50
		if (paymentChannel == PAYMENT_OPTION_FPX) {
			serviceFee = 1;
		}

		if (paymentChannel == PAYMENT_OPTION_FPX_ENTERPRISE) {
			serviceFee = 1.5;
		}
	}

	const totalAmountBeforeServiceFee = totalCharge - totalPromo;
	const totalAmount = Math.max(0, Number(
		(totalAmountBeforeServiceFee + serviceFee + bnplServiceFee).toFixed(2)),
	);

	const bankInstallmentFee = Math.max(0, Number(
		(eppRate * totalAmountBeforeServiceFee).toFixed(2)),
	);
	const bankInstallmentTotalAmount = Math.max(0, Number(
		(totalAmount + bankInstallmentFee).toFixed(2)),
	);

	const shopbackFee = 0;
	const shopbackTotalAmount = Math.max(0, Number((totalAmount + shopbackFee).toFixed(2)));	

	return {
		grossPremium,
		taxAmount,
		totalNetPremium,
		platformDiscountRateAmount,
		roadtaxAmount,
		totalCharge,
		totalPromo,
		serviceFee,
		totalAmount,
		totalAmountBeforeServiceFee,
		shopbackFee,
		shopbackTotalAmount,
		bankInstallmentFee,
		bankInstallmentTotalAmount,
		bnplServiceFee,
		psCarePlusFee
	};
}

function getTotalAddOnPrice(selectedAddOn: any[], addOnFormGroup: UntypedFormGroup, cartAddOnPriceList?: MotorAddOnCARTPriceInterface[]) {
	let total = 0;

	selectedAddOn.forEach((x) => {
		if (x.isIncludeSST) {
			if (x.code == "AdditionalDrivers")
				total += getAddOnPrice(x, getFormArray(addOnFormGroup).length);
			else if (x.category == "Windshield")
				total += getAddOnPrice(x, addOnFormGroup.value.windshield);
			else if (x.category == "FGAP") {
				if (addOnFormGroup.value.fgap) {
					total += addOnFormGroup.value.fgap;
				} else {
					total += getAddOnPrice(x, "0");
				}
			} else if (x.category == "LLTP") {
				total += getAddOnPrice(x, addOnFormGroup.value.lltpSeats);
			}
			else if (x.category == "CART")
				total += getAddOnPrice(x, null, addOnFormGroup.value.cartDays, addOnFormGroup.value.cartDailyCoverage, cartAddOnPriceList);
			else if (x.category == "GCKT")
				total += getAddOnPrice(x, addOnFormGroup.value.gckt);
			else if (x.category == "VA")
				total += getAddOnPrice(x, addOnFormGroup.value.va);
			else if (x.customCode === "UnlimitedTowing_PA")
          total += getAddOnPrice(x, addOnFormGroup.value.lltpSeats);
			else if (x.customCode === "Generali_UnlimitedDrivers")
				total += getAddOnPrice(x, Number(x.evaluate), null, null, null, selectedAddOn);
			// Already included in the quotation's totalBasePremium
			else if (x.code == "AllRiders") total += 0;
			else total += getAddOnPrice(x);
		}

	});
	return Number(total.toFixed(2));
}

function getTotalNonSSTAddOnPrice(selectedAddOn: any[], numberOfLltpSeats: number) {
    let total = 0;
    const filteredAddOns = selectedAddOn.filter(
      (x) => !x.isIncludeSST
    );
    filteredAddOns.forEach((x) => {
      if (x.customCode === "UnlimitedTowing_PA") {
        total += getAddOnPrice(x, numberOfLltpSeats);
      }
      else{
        total += getAddOnPrice(x);
      }
    });
    return total;
  }

function getTotalPromo(promo: any, grossPremium: number) {
	if (!promo) return 0;

	let evaluate = promo.evaluate;
	evaluate = evaluate.replace("{TotalGrossPremium}", grossPremium.toString());
	return Number(eval(evaluate).toFixed(2));
}

export function getAddOnPrice(item: ProductAddOnInterface, val?: any, selectedCartDays?: string, selectedCartDailyCoverage?: string, cartAddOnPriceList?: MotorAddOnCARTPriceInterface[], selectedAddOn?: any[]) {
	try {
		let evaluate = item.evaluate;
		let customEvaluate = item.customEvaluate
		let condition = item.condition;

		if (item.code == "FGAP") {
			const priceList = eval(evaluate);
			const valNum = parseInt(val);
			return Number(priceList[valNum].toFixed(2));
		}

		if (
    item.customCode === "Generali_UnlimitedDrivers" &&
    selectedAddOn.some((x) => x.code === "Flood"))
    {
      evaluate = customEvaluate.replace("{val}", val);
			return Number(eval(evaluate).toFixed(2));
    }

		// [PSSR-3203]: tiered windscreen rate based on entered Windscreen SI
		if (item.windscreenRate && val) {
			const { threshold, rateBelowThreshold, rateAboveThreshold } =
				item.windscreenRate;
			evaluate = `{val} * ${
				Number(val) < threshold ? rateBelowThreshold : rateAboveThreshold
			}`;
		}

		if (val) {
			evaluate = evaluate.replace("{val}", val);

			if (condition) {
				condition = condition.replace("{val}", val);
				if (item.isConditionFirst) {
					let status = Boolean(eval(condition));
					if (status) {
						return Number(eval(evaluate).toFixed(2));
					} else {
						return 0;
					}
				}
			}
		}

		if (item.category === "CART") {
			const cartPrice = cartAddOnPriceList.filter((x) => x.days.toString() == selectedCartDays && x.coverage.toString() == selectedCartDailyCoverage)
			evaluate = cartPrice.length > 0 ? cartPrice[0]?.price.toString() : null;
		}

		return Number(eval(evaluate).toFixed(2));
	} catch {
		return 0;
	}
}

function getFormArray(addOnFormGroup: UntypedFormGroup) {
	return addOnFormGroup.get("drivers") as UntypedFormArray;
}

function customSTMBRound(num: number): number {
	const decVal = parseFloat((num % 1).toFixed(2)); // Get 2 decimal point
	const decSubVal = parseFloat((num % 1).toFixed(2).substring(0, 3)); // Get 1 decimal point

	if (decVal >= parseFloat((decSubVal + 0.01).toFixed(2)) && decVal <= parseFloat((decSubVal + 0.02).toFixed(2))) {
		return Math.floor(num) + decSubVal;
	} else if (decVal >= parseFloat((decSubVal + 0.03).toFixed(2)) && decVal <= parseFloat((decSubVal + 0.05).toFixed(2))) {
		return Math.floor(num) + (decSubVal + 0.05);
	} else if (decVal >= parseFloat((decSubVal + 0.06).toFixed(2)) && decVal <= parseFloat((decSubVal + 0.07).toFixed(2))) {
		return Math.floor(num) + (decSubVal + 0.05);
	} else if (decVal >= parseFloat((decSubVal + 0.08).toFixed(2)) && decVal <= parseFloat((decSubVal + 0.09).toFixed(2))) {
		return Math.floor(num) + (decSubVal + 0.10);
	} else {
		return num;
	}
}
